import { useCallback, useEffect, useRef, useState } from "react";
import { Bell, X } from "lucide-react";
import { Link } from "react-router-dom";

import { formatDate } from "../lib/format";
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationsRead,
  notificationDraftHref,
  notificationHref,
  SEVERITY_META,
  SEVERITY_ORDER,
  type NotificationCenter,
  type NotificationItem,
  type NotificationSeverity,
} from "../lib/notifications";
import { ROLE_LABELS } from "../lib/roles";

/**
 * Центр уведомлений (E10, ТЗ 4.1.4: «уведомления собраны в едином центре») — колокольчик со
 * счётчиком в шапке и раскрывающаяся панель.
 *
 * Что показывать, решает СЕРВЕР: он отдаёт только сигналы, адресованные ролям пользователя
 * (Приложение Г), уже отсортированные по критичности, с готовыми русскими текстами. Клиент не
 * фильтрует и не сочиняет формулировок — иначе центр и будущая почтовая рассылка (фаза 2)
 * разошлись бы в словах.
 *
 * Счётчик считает только АКТУАЛЬНЫЕ непрочитанные: сигнал, снятый сторнированием, остаётся в
 * списке со штампом «неактуально», но в счётчик не идёт (Р-33).
 *
 * ОТКАЗ ЗАГРУЗКИ — отдельное состояние, а не «уведомлений нет». В системе контроля сроков это
 * принципиально: «просрочек у вас нет» и «мне не ответил сервер» — разные утверждения, и первое
 * вместо второго усыпляет ровно там, где нужно встревожить.
 *
 * Оформление — токены docs/07 (правило 9), доступность — по ТЗ 4.1.4: панель это `dialog` с
 * подписью, состояние кнопки — `aria-expanded`, счётчик озвучивается текстом, закрытие по Esc и
 * по клику вне панели, фокус переводится в панель и возвращается на кнопку.
 */

/** Порядок групп в панели — тот же, что у сортировки сервера (критичность сверху). */
const SEVERITY_GROUPS: readonly NotificationSeverity[] = SEVERITY_ORDER;

/** Заголовки групп — множественное число, чтобы читалось как раздел, а не как штамп строки. */
const GROUP_TITLES: Record<NotificationSeverity, string> = {
  escalation: "Эскалации",
  overdue: "Просрочки",
  legal: "Правовые сигналы",
  soon: "Приближаются сроки",
};

/** Оформление незнакомой критичности: нейтральное. Ответ сервера — не повод уронить шапку. */
const FALLBACK_META = { label: "Уведомление", dot: "bg-muted", stamp: "text-muted bg-control-bg" };

/**
 * Как часто счётчик переспрашивает сервер, мс.
 *
 * Минута — компромисс: событийный сигнал о черновике доходит достаточно быстро (разбор и так
 * идёт 20–70 секунд), а нагрузка остаётся пустяковой. Чаще незачем: сигналы этой системы про
 * сроки в днях, а не про секунды.
 */
const REFRESH_INTERVAL_MS = 60_000;

export function NotificationBell() {
  const [center, setCenter] = useState<NotificationCenter | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLButtonElement>(null);

  const load = useCallback(async () => {
    const res = await fetchNotifications();
    if (res.ok) {
      setCenter(res.value);
      setLoadError(null);
      return;
    }
    // Прежнее содержимое не затираем: показать вчерашний список честнее, чем пустоту.
    setLoadError(res.message);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * ПЕРИОДИЧЕСКОЕ ОБНОВЛЕНИЕ — дефект приёмки 15.08.2026.
   *
   * До E11 все сигналы рождались ночным тиком в 07:00: к моменту входа они уже лежали в базе, и
   * счётчик, снятый один раз при загрузке страницы, был верен весь день. Сигнал «черновик на
   * верификацию» — ПЕРВЫЙ событийный (Р-37): он появляется через 20–70 секунд после того, как
   * человек отправил комплект, и человек всё это время сидит на той же странице. Счётчик,
   * снятый до отправки, так и оставался нулём — на приёмке это выглядело как «сигнал не пришёл»,
   * хотя в базе он был.
   *
   * Обновляем раз в минуту и при возврате на вкладку. Пока вкладка скрыта, опроса нет: сигналы
   * не срочнее минуты, а фоновая вкладка не должна дёргать сервер сутками. Когда появится
   * второй событийный сигнал, это правило уже будет на месте.
   */
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === "visible") void load();
    };
    const timer = setInterval(tick, REFRESH_INTERVAL_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [load]);

  // Esc закрывает панель; фокус возвращается на колокольчик (ТЗ 4.1.4: работа с клавиатуры).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closePanel();
    };
    const onPointer = (e: MouseEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target) || bellRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [open]);

  // При открытии фокус уходит в панель — иначе клавиатурный пользователь остаётся «снаружи».
  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  const unread = center?.unread ?? 0;
  const unknownState = center === null && loadError !== null;

  function closePanel() {
    setOpen(false);
    bellRef.current?.focus();
  }

  async function openPanel() {
    setOpen(true);
    setActionError(null);
    await load(); // при открытии показываем свежее состояние, а не снимок момента входа
  }

  async function markRead(ids: readonly string[]) {
    if (ids.length === 0 || busy) return;
    setBusy(true);
    setActionError(null);
    const res = await markNotificationsRead(ids);
    if (!res.ok) setActionError(res.message);
    await load();
    setBusy(false);
  }

  async function markAll() {
    if (busy) return;
    setBusy(true);
    setActionError(null);
    const res = await markAllNotificationsRead();
    if (!res.ok) setActionError(res.message);
    await load();
    setBusy(false);
  }

  /** Подпись колокольчика: неизвестное состояние отличаем от «непрочитанных нет». */
  const bellLabel = unknownState
    ? "Уведомления: не удалось загрузить"
    : unread > 0
      ? `Уведомления: ${unread} непрочитанных`
      : "Уведомления: непрочитанных нет";

  const items = center?.items ?? [];
  const groups: { key: string; title: string; items: NotificationItem[] }[] = SEVERITY_GROUPS.map(
    (severity) => ({
      key: severity,
      title: GROUP_TITLES[severity],
      items: items.filter((i) => i.severity === severity),
    }),
  ).filter((g) => g.items.length > 0);

  // Незнакомая критичность (сервер обновили, клиент — ещё нет) НЕ должна молча пропасть: потерять
  // уведомление в системе контроля сроков хуже, чем показать его в нейтральной группе.
  const unknown = items.filter((i) => !SEVERITY_GROUPS.includes(i.severity));
  if (unknown.length > 0) groups.push({ key: "unknown", title: "Прочие", items: unknown });

  return (
    <div className="relative">
      <button
        ref={bellRef}
        type="button"
        onClick={() => void (open ? closePanel() : openPanel())}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls="notification-center"
        aria-label={bellLabel}
        className="inline-flex items-center gap-1.5 rounded-[var(--radius-m)] border border-top-field-border bg-top-field px-3 py-1.5 text-[13px] text-top-fg hover:text-surface"
      >
        <Bell size={16} aria-hidden="true" />
        {unknownState ? (
          <span aria-hidden="true" className="text-[13px] font-bold text-soon-fg">
            !
          </span>
        ) : (
          unread > 0 && (
            <span className="num rounded-full bg-overdue-fg px-1.5 text-[11px] font-bold text-surface">
              {unread}
            </span>
          )
        )}
      </button>

      {open && (
        <div
          id="notification-center"
          ref={panelRef}
          role="dialog"
          aria-label="Центр уведомлений"
          tabIndex={-1}
          className="absolute right-0 z-20 mt-2 max-h-[70vh] w-[min(460px,90vw)] overflow-y-auto rounded-[var(--radius-m)] border border-border bg-surface text-ink shadow-lg"
        >
          <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-2">
            <b className="doc-title text-[15px]">Уведомления</b>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button
                  type="button"
                  onClick={() => void markAll()}
                  disabled={busy}
                  className="text-[12px] font-bold text-accent underline disabled:opacity-50"
                >
                  Отметить все
                </button>
              )}
              <button
                type="button"
                onClick={closePanel}
                aria-label="Закрыть уведомления"
                className="rounded-[var(--radius-m)] border border-border px-1.5 py-1 text-muted hover:text-ink"
              >
                <X size={13} aria-hidden="true" />
              </button>
            </div>
          </div>

          {center?.affiliatedMuted && (
            <p className="border-b border-border bg-soon-bg px-4 py-2 text-[12px] text-soon-fg">
              Администратор включил приглушение по аффилированным контрагентам: эскалации и
              претензионные сигналы по внутригрупповым договорам не формируются.
            </p>
          )}

          {loadError !== null && (
            <div role="alert" className="border-b border-border bg-overdue-bg px-4 py-2">
              <p className="text-[12px] text-overdue-fg">
                Не удалось загрузить уведомления: {loadError} Показанное ниже может быть неполным.
              </p>
              <button
                type="button"
                onClick={() => void load()}
                className="mt-1 text-[12px] font-bold text-accent underline"
              >
                Повторить
              </button>
            </div>
          )}

          {actionError !== null && (
            <p
              role="alert"
              className="border-b border-border px-4 py-2 text-[12px] text-overdue-fg"
            >
              {actionError}
            </p>
          )}

          {center === null && loadError === null ? (
            <p className="px-4 py-3 text-[13px] text-muted" role="status">
              Загрузка уведомлений…
            </p>
          ) : groups.length === 0 && loadError === null ? (
            <p className="px-4 py-3 text-[13px] text-muted">
              Уведомлений нет. Сюда попадают напоминания о сроках, просрочки и правовые сигналы —
              каждому по его роли.
            </p>
          ) : (
            groups.map((group) => (
              <section key={group.key} aria-label={group.title}>
                <h3 className="border-b border-border bg-bg px-4 py-1 text-[11px] font-bold uppercase tracking-[0.04em] text-muted">
                  {group.title}
                </h3>
                <ul className="divide-y divide-border">
                  {group.items.map((item) => (
                    <li key={item.id}>
                      <NotificationRow item={item} busy={busy} onRead={markRead} />
                    </li>
                  ))}
                </ul>
              </section>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Строка уведомления. Если объект доступен — это ССЫЛКА: к договору, к обязательству или на
 * верификацию черновика. Объект недоступен (договор удалён) — обычная кнопка: только отметить
 * прочитанным. Нажатие в обоих случаях помечает сигнал прочитанным: человек его увидел, и
 * требовать отдельного действия «я прочитал» — лишняя работа.
 *
 * «ПРОЧИТАНО» И «НЕАКТУАЛЬНО» — РАЗНЫЕ СОСТОЯНИЯ, И РАЗЛИЧАТЬ ИХ НАДО СИЛЬНЕЕ (постановка
 * 16.08.2026). Первое про ЧЕЛОВЕКА: он сигнал видел. Второе про СОБЫТИЕ: обязательство исполнено
 * или сторнировано, и сигнал больше не отражает действительность (Р-33).
 *
 * Отсюда два независимых оформления:
 *   • непрочитанное — белый фон и жирный заголовок, прочитанное — серый фон и обычный шрифт;
 *   • неактуальное — зачёркнутый заголовок и штамп «неактуально», НЕЗАВИСИМО от прочтения.
 *
 * Раньше пометка «новое» пряталась у неактуальных (`!item.read && !item.stale`), и неактуальный
 * непрочитанный сигнал выглядел как прочитанный — то есть система молча решала за человека, что
 * читать это уже незачем. Из СЧЁТЧИКА неактуальные по-прежнему уходят (Р-33): это отдельное
 * правило, и оно не меняется.
 */
function NotificationRow({
  item,
  busy,
  onRead,
}: {
  item: NotificationItem;
  busy: boolean;
  onRead: (ids: readonly string[]) => Promise<void>;
}) {
  const href = notificationHref(item);
  const draftHref = notificationDraftHref(item);
  const meta = SEVERITY_META[item.severity] ?? FALLBACK_META;
  // Подпись адресата: роль — «за кого из отделов», «лично Вам» — «за Вас как за Инициатором
  // договора» (E17). Личный сигнал приходит ВДОБАВОК к ролевому, и без этой пометки два похожих
  // сообщения в панели выглядели бы дублем, а не разной ответственностью.
  const addresseeLabel = item.personal
    ? "лично Вам"
    : item.role === null
      ? ""
      : (ROLE_LABELS[item.role] ?? item.role);
  const cls = `flex w-full gap-2 px-4 py-2.5 text-left hover:bg-control-bg ${
    item.read ? "bg-bg" : "bg-surface"
  }`;

  const body = (
    <>
      <span
        aria-hidden="true"
        className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${meta.dot} ${item.stale ? "opacity-40" : ""}`}
      />
      <span className="min-w-0">
        <span
          className={`block text-[13px] ${item.read ? "font-normal" : "font-bold"} ${
            item.stale ? "text-muted line-through" : "text-ink"
          }`}
        >
          {item.title}
        </span>
        <span className="block text-[12px] text-muted">{item.text}</span>
        <span className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-muted">
          <span className={`stamp ${meta.stamp}`}>{meta.label}</span>
          {addresseeLabel !== "" &&
            (item.personal ? (
              <span className="stamp bg-control-bg text-accent">{addresseeLabel}</span>
            ) : (
              <span>{addresseeLabel}</span>
            ))}
          <span className="num">{formatDate(item.day)}</span>
          {item.stale && <span className="stamp bg-waiting-bg text-waiting-fg">неактуально</span>}
          {!item.read && <span className="font-bold text-accent">новое</span>}
        </span>
      </span>
    </>
  );

  const row =
    href === null ? (
      <button
        type="button"
        onClick={() => void onRead([item.id])}
        disabled={busy}
        className={`${cls} disabled:opacity-60`}
      >
        {body}
      </button>
    ) : (
      <Link to={href} onClick={() => void onRead([item.id])} className={cls}>
        {body}
      </Link>
    );

  if (draftHref === null) return row;

  /*
   * ВТОРАЯ ССЫЛКА — «Открыть разбор» (решение владельца 17.09.2026). Стои́т ОТДЕЛЬНОЙ строкой под
   * основной, а не внутри неё: ссылка внутри ссылки — недопустимая разметка, и переход по ней
   * зависел бы от того, куда именно попал курсор. Первая ведёт в договор (что встало на
   * контроль), вторая — в разбор (что предлагала модель и что поправил Инициатор).
   */
  return (
    <div className={item.read ? "bg-bg" : "bg-surface"}>
      {row}
      <Link
        to={draftHref}
        onClick={() => void onRead([item.id])}
        className="block px-4 pb-2.5 pl-8 text-[12px] font-bold text-accent hover:underline"
      >
        Открыть разбор
      </Link>
    </div>
  );
}
